// Every word the app shows, in English (SW-27). es.ts has the same shape, so a
// missing Spanish string is a type error. Names people type (events, cooks,
// dishes, categories) are never translated.

export function ordinal(place: number): string {
  const tens = place % 100;
  if (tens >= 11 && tens <= 13) return `${place}th`;
  return `${place}${({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[place % 10] ?? "th"}`;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const en = {
  /** The language's own name, on the switch that leads to it. */
  name: "English",
  /** For dates; the phone's own locale wins when it's the same language. */
  locale: "en-US",

  app: {
    notFound: "Page not found",
    goHome: "Go to the home page",
    oops: "Hmm.",
    home: "Home",
    loginFirst: "Please log in first.",
    notYours: "That event belongs to someone else.",
    noEvent: "We couldn't find that event.",
    offline: "Something went wrong. Check your connection and try again.",
    generic: "Something went wrong. Please try again.",
    helpLink: "Help and questions",
    helpHref: "/help/",
    printablesLink: "Free printables",
    printablesHref: "/printables/",
    partOf: "Part of",
  },

  common: {
    cancel: "Cancel",
    save: "Save",
    entry: (n: number) => `Entry ${n}`,
    by: (cook: string) => `by ${cook}`,
    votes: (n: number) => plural(n, "vote", "votes"),
    place: (n: number, tied: boolean) => `${ordinal(n)} place${tied ? " (tied)" : ""}`,
    coChampions: "Co-champions!",
    noDishes: "No dishes yet.",
    nameLabel: "Name ",
    dateLabel: "Date ",
    cookLabel: "Cook ",
    dishLabel: "Dish ",
    // SW-59: the "made with" line on results and the reveal.
    runYourOwn: "Run your own cookoff, free",
  },

  status: {
    setup: "Setting up",
    open: "Voting open",
    closed: "Voting closed",
    revealed: "Results out",
  } as Record<string, string>,

  home: {
    tagline: "Blind tasting, honest votes, and winners revealed 3rd to 1st.",
    hereToVote: "Here to vote?",
    typeCode: "Type the code from the poster ",
    codePlaceholder: "e.g. K7QM2X",
    go: "Go",
    runningCookoff: "Running a cookoff?",
    loginIntro: "Log in with your email. We'll send you a link, so there's no password.",
    linkExpired: "That login link has expired or was already used. Send yourself a new one.",
    emailLabel: "Email ",
    emailMeLink: "Email me a link",
    sending: "Sending…",
    checkEmail: (email: string) => `Check ${email} for your login link. It works once and expires in 15 minutes.`,
    sendFailed: "Couldn't send the link. Try again.",
    newCookoff: "New cookoff",
    namePlaceholder: "Bailey Family Chili Cookoff",
    create: "Create",
    kind: "Kind of cookoff",
    family: "Family",
    familyHint: "Guests vote for their favourite dish.",
    pro: "Pro",
    proHint: "Judges score every dish blind.",
    comingSoon: "(coming soon)",
    loggedInAs: (email: string) => `Logged in as ${email} `,
    logout: "Log out",
    yourCookoffs: "Your cookoffs",
    coOrganizer: "co-organizer",
    noneYet: "None yet. Create one above.",
    noSuchCode: "No such code",
    noSuchCodeBody: (code: string) => `We couldn't find a cookoff with the code ${code}. Check the poster and try again.`,
  },

  vote: {
    notOpenYet: "Voting hasn't opened yet. Have a look at the dishes, and come back when the organizer opens it.",
    tapFavourite: "Tap your favourite dish. You can change your mind until voting closes.",
    closedSoon: "Voting is closed. The results are coming soon!",
    voting: "Voting…",
    saved: "Vote saved. Thanks!",
    hasClosed: "Voting has closed.",
    yourVote: "✓ Your vote",
    entryAria: (n: number, dish: string) => `Entry ${n}, ${dish}`,
    loadFailed: "Couldn't load the vote check. Check your connection and try again.",
    checkFailed: "The vote check failed. Please try again.",
  },

  manage: {
    back: "← Your cookoffs",
    steps: { setup: "Set up", open: "Voting", closed: "Closed", revealed: "Results" } as Record<string, string>,
    progress: "Event progress",
    setupHint: "Add the dishes, print the poster, then open voting.",
    openVoting: "Open voting",
    isOpen: "Voting is open.",
    closeVoting: "Close voting",
    closeTitle: "Close voting?",
    closeBody: "Guests can't vote until you reopen it.",
    closedHint: "Voting is closed. Totals stay hidden from guests until the reveal.",
    startReveal: "Start the reveal",
    revealTitle: "Reveal the winners?",
    revealBody: "The reveal shows the winners, 3rd place first. When the champion appears, the results go public on the guests' link.",
    revealConfirm: "Reveal the winners",
    reopen: "Reopen voting",
    reopenAside: "Closed too soon? Reopen voting: every vote so far is kept.",
    resultsOut: "The results are out.",
    seeResults: "See the results page",
    undo: "Undo the reveal",
    undoTitle: "Undo the reveal?",
    undoBody:
      "The results page goes back to \"results coming soon\". If you then reopen voting, the standings people have already seen could change.",
    votesCast: "Votes cast",
    peek: "Peek at the standings",
    hidePeek: "Hide the standings",
    peekRow: (n: number, dish: string, cook: string, votes: number) => `Entry ${n}, ${dish} (${cook}): ${votes}`,
    noPhoto: "No photo",
    edit: "Edit",
    delete: "Delete",
    deleteEntry: (n: number, dish: string) => `Delete entry ${n}, ${dish}?`,
    newPhoto: "New photo ",
    photo: "Photo ",
    adding: "Adding…",
    addDish: "Add a dish",
    add: "Add",
    dishes: "Dishes",
    renameCategory: "Rename category",
    rename: "Rename",
    deleteCategory: "Delete category",
    deleteCategoryConfirm: (name: string) => `Delete the ${name} category and its dishes?`,
    nameCategory: "Name the new category",
    nameFirstCategory: "Name the new category (your current dishes become the first one)",
    categoryExample: "Dessert",
    addCategory: "Add another category",
    splitCategories: "Split into categories (e.g. Chili and Dessert)",
    organizers: "Organizers",
    createdIt: " (created it)",
    remove: "Remove",
    inviteLabel: "Invite a co-organizer by email ",
    invite: "Invite",
    shareAndPrint: "Share and print",
    votingPage: "Voting page: ",
    typedCode: "Typed code: ",
    printPoster: "Print the poster",
    printCards: "Print the table cards",
    editDetails: "Edit name or date",
    deleteEvent: "Delete this event",
    deleteEventConfirm: (name: string) => `Delete ${name} for good, with all its photos and votes?`,
    photoFailed: "Could not process that photo",
  },

  reveal: {
    back: "← Back to the event",
    notYet: "Not yet",
    closeFirst: "Close voting first, then come back to reveal the winners.",
    tapFirst: "Tap for 3rd place",
    nothing: "There are no dishes to reveal.",
    champion: "Champion!",
    cookedBy: "Cooked by",
    tapNext: "Tap for the next place",
    congrats: "🎉 Congratulations to every cook!",
  },

  print: {
    back: "← Back",
    print: "Print",
    letter: "Sized for US Letter paper",
    voteFavourite: "Vote for your favourite dish!",
    scanOrGo: "Scan with your phone's camera, or go to",
    enterCode: "and enter the code ",
    scanToVote: "Scan to vote",
    addDishesFirst: "Add some dishes first.",
    runYourOwnAt: (host: string) => `Run your own cookoff, free at ${host}`,
  },
};

export type Messages = typeof en;
